import { ArrowLeft, Globe, Smartphone, GraduationCap, Wrench, Code, Lightbulb, Mail, Phone, Send, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import '../styles/futuristic.css';
import { useState, useEffect } from 'react';
import lumitechLogo from '../../logos/lumitech-darkbluenew.jpeg';

interface LumiTechProps {
  onNavigate: (page: 'landing' | 'tech' | 'clean' | 'learn') => void;
  onToggleTheme: () => void;
  isDarkMode: boolean;
}

function LumiTech({ onNavigate, onToggleTheme, isDarkMode }: LumiTechProps) {
  const [formData, setFormData] = useState({ name: '', contact: '', message: '' });
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    document.title = 'LumiTech | Website & App Development | Fort Wayne, IN';
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = `LumiTech Inquiry from ${formData.name}`;
    const body = `Name: ${formData.name}\nContact: ${formData.contact}\n\nMessage:\n${formData.message}`;
    window.location.href = `mailto:portialane@lumiworksllc.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setIsSubmitted(true);
  };

  const handleCallClick = () => {
    setIsSubmitted(true);
  };

  const services = [
    { 
      icon: <Globe className="w-8 h-8" />, 
      title: "Website Creation", 
      description: "Custom websites built for your needs" 
    },
    { 
      icon: <Smartphone className="w-8 h-8" />, 
      title: "App Creation", 
      description: "Mobile and web application development" 
    },
    { 
      icon: <GraduationCap className="w-8 h-8" />, 
      title: "Adult Tech Literacy", 
      description: "Technology education and training for adults" 
    },
    { 
      icon: <Wrench className="w-8 h-8" />, 
      title: "Tech Maintenance", 
      description: "Device and system maintenance services" 
    },
    { 
      icon: <Code className="w-8 h-8" />, 
      title: "Programming", 
      description: "Software development and coding services" 
    }
  ];

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0">
        <div className={`absolute inset-0 transition-colors duration-300 ${isDarkMode ? 'bg-gray-950' : 'bg-gray-50'}`}></div>
        <div className={`glow-sphere ${isDarkMode ? 'glow-silver' : 'glow-silver-light'} w-96 h-96 top-20 -left-40 animate-glow`}></div>
        <div className={`glow-sphere ${isDarkMode ? 'glow-silver' : 'glow-silver-light'} w-80 h-80 bottom-20 -right-40 animate-glow`} style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="relative z-10 container mx-auto px-4 sm:px-6 py-12">
        <motion.div 
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="flex justify-between items-center mb-12 glass-panel p-4 rounded-2xl"
        >
          <button
            onClick={() => onNavigate('landing')}
            className={`flex items-center ${isDarkMode ? 'text-slate-200 hover:text-slate-50' : 'text-slate-700 hover:text-slate-900'} font-semibold transition-colors animate-slide-in-left`}
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </button>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={onToggleTheme}
            className={`p-2 rounded-full ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100'} shadow-md transition-all`}
            aria-label="Toggle dark mode"
          >
            {isDarkMode ? (
              <Lightbulb className="w-5 h-5 text-yellow-300" />
            ) : (
              <Lightbulb className="w-5 h-5 text-yellow-600" />
            )}
          </motion.button>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center mb-16"
          >
            <motion.h1 
              className={`text-5xl md:text-6xl font-bold mb-4 bg-clip-text text-transparent ${isDarkMode ? 'bg-gradient-to-r from-blue-300 to-blue-500' : 'bg-gradient-to-r from-blue-600 to-blue-800'}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              LumiTech
            </motion.h1>
            
            <motion.p 
              className={`text-2xl font-medium mb-8 ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Website & App Creation · Adult Tech Literacy · Tech Maintenance · Programming
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex flex-wrap justify-center gap-4 mt-8"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                className={`px-6 py-3 rounded-full font-medium flex items-center ${isDarkMode ? 'bg-slate-200 hover:bg-slate-100 text-gray-900' : 'bg-slate-700 hover:bg-slate-800 text-white'}`}
              >
                Contact Us
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => document.getElementById('about-section')?.scrollIntoView({ behavior: 'smooth' })}
                className={`px-6 py-3 rounded-full font-medium ${isDarkMode ? 'bg-transparent border-2 border-slate-400 text-slate-200 hover:bg-slate-900/40' : 'bg-transparent border-2 border-slate-500 text-slate-700 hover:bg-slate-100'}`}
              >
                Learn More
              </motion.button>
            </motion.div>
          </motion.div>

          <div id="about-section" className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-white/90'} backdrop-blur-md rounded-2xl shadow-xl p-8 mb-8 border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} animate-fade-in-up delay-100`}>
            <h2 className={`text-3xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>About LumiTech</h2>
            <p className={`leading-relaxed mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              LumiTech provides technology services including website and app creation, 
              adult tech literacy training, tech maintenance, and programming services.
            </p>
            <p className={`leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              We help individuals and businesses with their technology needs, from building 
              digital solutions to teaching essential tech skills.
            </p>
          </div>

          {/* Services Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <h2 className={`text-3xl font-bold text-center mb-12 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Our Services</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.3 }}
                  className={`glass-panel p-6 rounded-2xl h-full flex flex-col ${isDarkMode ? 'bg-gray-800/80' : 'bg-white/90'}`}
                >
                  <div className={`p-3 rounded-lg inline-flex ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-100'} mb-4`}>
                    {service.icon}
                  </div>
                  <h3 className={`text-xl font-semibold mb-2 flex-grow ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {service.title}
                  </h3>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {service.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Location Notice */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`text-center p-6 rounded-xl mb-8 ${isDarkMode ? 'bg-gray-800/50' : 'bg-white/80'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
          >
            <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <span className="font-semibold">Service Area:</span> All services available in 
              <span className={`font-bold ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}> Fort Wayne and surrounding areas</span>.
            </p>
          </motion.div>

          {/* Contact Section */}
          <motion.div 
            id="contact-form"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`rounded-2xl p-8 mb-8 ${isDarkMode ? 'bg-gray-800/80 border border-gray-700' : 'bg-white/90 border border-gray-200'} shadow-xl`}
          >
            <h2 className={`text-3xl font-bold text-center mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Get in Touch</h2>
            <p className={`text-center mb-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Fill out the form below and we'll get back to you shortly.
            </p>

            {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Name</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
                  placeholder="Your name"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Email or Phone</label>
                <input
                  type="text"
                  required
                  value={formData.contact}
                  onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                  className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
                  placeholder="your@email.com or (260) 555-0123"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Message</label>
                <textarea
                  required
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
                  placeholder="How can we help you?"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`flex items-center justify-center gap-2 px-8 py-3 rounded-lg font-semibold transition-all ${isDarkMode ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                >
                  <Send className="w-5 h-5" />
                  Send via Email
                </motion.button>
                
                <motion.a
                  href="tel:2602800434"
                  onClick={handleCallClick}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`flex items-center justify-center gap-2 px-8 py-3 rounded-lg font-semibold transition-all ${isDarkMode ? 'bg-green-600 hover:bg-green-500 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}`}
                >
                  <Phone className="w-5 h-5" />
                  Call or Text (260) 280-0434
                </motion.a>
              </div>

              <div className={`text-center pt-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <p className="flex items-center justify-center gap-2">
                  <Mail className="w-4 h-4" />
                  portialane@lumiworksllc.com
                </p>
              </div>
            </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, type: "spring" }}
                className={`text-center py-12 rounded-xl ${isDarkMode ? 'bg-green-900/30 border border-green-500/30' : 'bg-green-50 border border-green-200'}`}
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                >
                  <CheckCircle className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                </motion.div>
                <h3 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Thank You!</h3>
                <p className={`text-lg max-w-md mx-auto ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  We'll contact you back shortly. Thank you for reaching out!
                </p>
                <motion.button
                  onClick={() => setIsSubmitted(false)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`mt-6 px-6 py-2 rounded-lg font-medium transition-all ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
                >
                  Send Another Message
                </motion.button>
              </motion.div>
            )}
          </motion.div>

        {/* Footer */}
        <footer className="text-center mt-12">
          <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>
            &copy; {new Date().getFullYear()} LumiTech. All rights reserved.
          </p>
        </footer>
        </div>
      </div>
    </div>
  );
}

export default LumiTech;
